import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  useMemo,
} from "react";
import type {
  OutlookPluginContextValue,
  OutlookPluginContextType,
  OutlookPluginAdapter,
} from "./types";
import type { MsofficeApiClient, MsofficeApiFunctions } from "../api/types";
import {
  useFeatureFlag,
  registerFeatureFlagApiClient,
} from "../../featureFlags/useFeatureFlag";
import { registerTelemetryApiClient } from "../../utils/telemetryApi";

const OutlookPluginContext = createContext<OutlookPluginContextValue | null>(
  null,
);

export const useOutlookPluginContext = (): OutlookPluginContextValue => {
  const context = useContext(OutlookPluginContext);
  if (!context) {
    throw new Error(
      "[OutlookPluginContext] useOutlookPluginContext called outside of OutlookPluginProvider, falling back to null",
    );
  }
  return context;
};

interface OutlookPluginProviderProps {
  children: React.ReactNode;
  context: OutlookPluginContextType;
  adapter: OutlookPluginAdapter;
  api: MsofficeApiFunctions;
  apiClient: MsofficeApiClient;
  initialMockMessageId?: string | null;
  initialMockSharedInbox?: string | null;
}

export const OutlookPluginProvider: React.FC<OutlookPluginProviderProps> = ({
  children,
  context: contextType,
  adapter,
  api,
  apiClient,
  initialMockMessageId = null,
  initialMockSharedInbox = null,
}) => {
  const [currentMessageId, setCurrentMessageId] = useState<string | null>(null);
  const [currentSharedInbox, setCurrentSharedInbox] = useState<string | null>(
    null,
  );
  const [mockMessageId, setMockMessageIdState] = useState<string | null>(
    initialMockMessageId,
  );
  const [mockSharedInbox, setMockSharedInboxState] = useState<string | null>(
    initialMockSharedInbox,
  );
  const [overrideShipmentId, setOverrideShipmentIdState] = useState<string | null>(null);
  const [isReady, setIsReady] = useState(false);
  const isDevMode = useMemo(() => adapter.isDevMode(), [adapter]);

  const refreshMessageId = useCallback(async () => {
    if (isDevMode) {
      console.log(
        "OutlookPluginContext: Dev mode - using mock message ID:",
        mockMessageId,
      );
      return;
    }
    if (adapter.isContextReady()) {
      const messageId = adapter.getCurrentMessageId();
      console.log("OutlookPluginContext: Refreshing message ID:", messageId);
      setCurrentMessageId(messageId);

      // Also refresh the shared inbox
      const sharedInbox = await adapter.getTargetMailbox();
      console.log(
        "OutlookPluginContext: Target mailbox (shared inbox):",
        sharedInbox,
      );
      setCurrentSharedInbox(sharedInbox);
    }
  }, [adapter, isDevMode, mockMessageId]);

  // Initialize context readiness
  useEffect(() => {
    if (contextType === "web" || isDevMode) {
      // Web context or dev mode - mark as ready immediately
      console.log("OutlookPluginContext: Running in web/dev mode");
      setIsReady(true);
      return;
    }

    // msoffice context - check adapter readiness
    const checkReady = () => {
      const ready = adapter.isContextReady();
      setIsReady(ready);
      if (ready) {
        refreshMessageId();
      }
    };

    // Initial check
    checkReady();

    // Poll for readiness if not ready yet
    if (!adapter.isContextReady()) {
      const pollInterval = setInterval(() => {
        if (adapter.isContextReady()) {
          clearInterval(pollInterval);
          checkReady();
        }
      }, 100);

      return () => clearInterval(pollInterval);
    }
  }, [contextType, adapter, isDevMode, refreshMessageId]);

  // Set up item changed handler for msoffice context
  useEffect(() => {
    if (contextType === "web" || isDevMode || !isReady) {
      return;
    }

    const handleItemChanged = () => {
      console.log("OutlookPluginContext: Item changed event fired");
      refreshMessageId();
    };

    if (adapter.addItemChangedHandler) {
      adapter.addItemChangedHandler(handleItemChanged);
    }

    return () => {
      if (adapter.removeItemChangedHandler) {
        adapter.removeItemChangedHandler(handleItemChanged);
      }
    };
  }, [contextType, isDevMode, isReady, adapter, refreshMessageId]);

  const setMockMessageId = useCallback(
    (id: string | null) => {
      setMockMessageIdState(id);
    },
    [],
  );

  const setMockSharedInbox = useCallback(
    (inbox: string | null) => {
      setMockSharedInboxState(inbox);
    },
    [],
  );

  const setOverrideShipmentId = useCallback(
    (id: string | null) => {
      setOverrideShipmentIdState(id);
    },
    [],
  );

  // Register API client for feature flags on mount
  useEffect(() => {
    registerFeatureFlagApiClient(apiClient);
    registerTelemetryApiClient(apiClient);
  }, [apiClient]);

  // Mock overrides when set; falls back to real Outlook value when cleared (null)
  const effectiveMessageId = mockMessageId ?? currentMessageId;
  const effectiveSharedInbox = mockSharedInbox ?? currentSharedInbox;

  const actions = useMemo(
    () => ({
      openEmail: adapter.openEmail,
      getCurrentMessageId: adapter.getCurrentMessageId,
      openUrl: adapter.openUrl,
      draftEmail: adapter.draftEmail,
    }),
    [adapter],
  );

  const contextValue: OutlookPluginContextValue = useMemo(
    () => ({
      contextType,
      currentMessageId: effectiveMessageId,
      currentSharedInbox: effectiveSharedInbox,
      isReady,
      isDevMode,
      actions,
      api,
      apiClient,
      setMockMessageId,
      setMockSharedInbox,
      overrideShipmentId,
      setOverrideShipmentId,
      refreshMessageId,
      useFeatureFlag,
    }),
    [
      contextType,
      effectiveMessageId,
      effectiveSharedInbox,
      isReady,
      isDevMode,
      actions,
      api,
      apiClient,
      setMockMessageId,
      setMockSharedInbox,
      overrideShipmentId,
      setOverrideShipmentId,
      refreshMessageId,
    ],
  );

  return (
    <OutlookPluginContext.Provider value={contextValue}>
      {children}
    </OutlookPluginContext.Provider>
  );
};
